import { useMemo } from 'react';
import { useModels, type LlmModel } from '@/hooks/useModels';

const CHARS_PER_TOKEN = 4;
const SYSTEM_PROMPT_CONTEXT_RATIO = 0.5;
const FALLBACK_TOKENS = 8000;

export interface ContextBudget {
  contextWindow: number;
  maxChars: number;
  modelLabel: string;
  isFallback: boolean;
}

function tokensToChars(tokens: number): number {
  return Math.floor(tokens * CHARS_PER_TOKEN * SYSTEM_PROMPT_CONTEXT_RATIO);
}

function isTextGenerationModel(m: LlmModel): boolean {
  const caps = (m.capabilities ?? {}) as Record<string, unknown>;
  if (caps.image_gen === true) return false;
  const modalities = m.input_modalities ?? [];
  return modalities.includes('text');
}

export function useContextBudget(modelId?: string | null): ContextBudget {
  const { data: models } = useModels();

  return useMemo(() => {
    if (modelId && models) {
      const model = models.find(m => m.id === modelId);
      if (model && model.context_window) {
        return {
          contextWindow: model.context_window,
          maxChars: tokensToChars(model.context_window),
          modelLabel: model.display_name,
          isFallback: false,
        };
      }
    }

    // Fallback: pior caso entre modelos recomendados de texto
    const candidates = (models ?? []).filter(
      m => m.is_recommended && m.is_active && isTextGenerationModel(m) && m.context_window,
    );

    if (candidates.length === 0) {
      return {
        contextWindow: FALLBACK_TOKENS,
        maxChars: tokensToChars(FALLBACK_TOKENS),
        modelLabel: 'previsão',
        isFallback: true,
      };
    }

    const worst = candidates.reduce((min, m) =>
      (m.context_window ?? Infinity) < (min.context_window ?? Infinity) ? m : min,
    );

    return {
      contextWindow: worst.context_window ?? FALLBACK_TOKENS,
      maxChars: tokensToChars(worst.context_window ?? FALLBACK_TOKENS),
      modelLabel: `previsão ${worst.display_name}`,
      isFallback: true,
    };
  }, [modelId, models]);
}
